import type { FileParseWarning } from '@/types/file.types';
import type { MessageWarning } from '@/types/chat.types';

/**
 * Build the warning shown on an assistant message from any pending file
 * warnings (PDF truncation, image-encode failure) plus an optional API
 * warning (e.g. token truncation). Returns the apiWarning unchanged when
 * there are no file warnings. The title adapts to which file issues
 * occurred so an image-encode failure isn't mislabeled as a truncation.
 */
export function buildUploadFileWarning(
  pendingFileWarnings: FileParseWarning[],
  apiWarning?: MessageWarning,
): MessageWarning | undefined {
  if (pendingFileWarnings.length === 0) return apiWarning;

  const hasEncodeFailure = pendingFileWarnings.some(
    (w) => w.code === 'image_encode_failed',
  );
  const hasTruncation = pendingFileWarnings.some(
    (w) =>
      w.code === 'pdf_truncated' ||
      w.code === 'xlsx_truncated' ||
      w.code === 'content_truncated',
  );

  const title =
    hasEncodeFailure && hasTruncation
      ? 'Some attachments had issues'
      : hasEncodeFailure
        ? 'Attachment not sent'
        : 'Uploaded file truncated';

  const message = [
    ...pendingFileWarnings.map((w) => w.message),
    apiWarning?.message,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    type: 'truncated',
    title,
    message,
    icon: 'AlertTriangle',
  };
}
